import type { ActivationLifecycle } from '@/infra/activation'
import { isSameDay } from 'date-fns'
import { action, computed, observable, reaction } from 'mobx'
import { uniqueId } from 'lodash-es'
import { binarySearch } from '@/utils/array-util'
import { Subject } from 'rxjs'

export interface ChatListViewModelOptions<M, V = M, E = unknown> {
  /**
   * Reactive getter for message instances.
   * Each message must preserve identity.
   */
  messages: () => M[]
  /**
   * Reactive getter for event instances.
   * Each event must preserve identity.
   */
  events?: () => E[]
  /**
   * Function for creating a VM for the message. If not specified, then
   * the actual message will also be treated as if it's the VM.
   */
  createMessageVm?: (message: M) => V
  /**
   * Function for getting the sender of a message.
   */
  getMessageSender: (message: M) => unknown
  /**
   * Function for getting the date of a message.
   */
  getMessageDate: (message: M) => Date
  /**
   * Function for getting the date of an event.
   */
  getEventDate?: (event: E) => Date
  /**
   * Activates the message item.
   */
  activateMessage?: (message: V) => unknown
  /**
   * Deactivates the message item.
   */
  deactivateMessage?: (message: V) => unknown
  /**
   * Activates the event item.
   */
  activateEvent?: (event: E) => unknown
  /**
   * Deactivates the event item.
   */
  deactivateEvent?: (event: E) => unknown
  /**
   * The maximum duration (in milliseconds) between messages from the same sender to be grouped together.
   */
  maxDurationBetweenSameSenderMessages?: number
}

/**
 * A view model that maintains elements for rendering the chat view based
 * on message view models. This handles inserting time markers and tracking
 * which messages should be grouped together.
 *
 * @template M The underlying message type
 * @template V The type of the view model to be created for each message
 * @template E The type of the event
 */
export class ChatListViewModel<M, V = M, E = unknown>
  implements ActivationLifecycle
{
  /**
   * Subject where new items will be emitted.
   *
   * @private
   */
  private readonly _itemAddedSubject = new Subject<
    ChatListMessageItemViewModel<M, V> | ChatListEventItemViewModel<E>
  >()

  /**
   * Observable stream where new, non-time marker items will be emitted.
   */
  readonly itemAdded = this._itemAddedSubject.asObservable()

  /**
   * Disposes the sync reaction.
   * @private
   */
  private _disposeReaction: (() => void) | null = null

  /**
   * Map of event view models used to preserve identity.
   *
   * @private
   */
  private _eventVmMap = new Map<E, ChatListEventItemViewModel<E>>()

  /**
   * Map of message view models used to preserve identity.
   *
   * @private
   */
  private _messageVmMap = new Map<M, ChatListMessageItemViewModel<M, V>>()

  /**
   * Map of time marker view models used to preserve identity.
   *
   * @private
   */
  private _timeMarkerVmMap = new Map<Date, ChatListTimeMarkerViewModel>()

  /**
   * Used to generate group IDs consistently for when messages are appended
   * and prepended.
   *
   * @private
   */
  private _epoch: Date | null = null

  /**
   * List of items, sorted by date ascending.
   * @private
   */
  @observable private accessor _items: ChatListItemViewModel<M, V, E>[] = []

  /**
   * Constructor.
   */
  constructor(private readonly _options: ChatListViewModelOptions<M, V, E>) {}

  /**
   * Gets the items.
   */
  @computed get items() {
    return this._items
  }

  /** @inheritDoc */
  activate() {
    this._disposeReaction = reaction(
      () => {
        const messages = this._options.messages()
        const events = this._options.events?.() ?? []

        // We'll need to access all the properties we're reacting to,
        // so it'll rerun when they change. This does incur some overhead,
        // but it prevents us from having to manually track the dependencies.
        // It won't have any significant impact on performance until we reach
        // roughly >5000 messages in a conversation.
        for (const message of messages) {
          this._options.getMessageDate(message)
          this._options.getMessageSender(message)
        }

        if (this._options.getEventDate) {
          for (const event of events) {
            this._options.getEventDate(event)
          }
        }

        return [messages, events] as const
      },
      ([messages, events]) => this._syncItems(messages, events),
      {
        fireImmediately: true,
      },
    )
  }

  /** @inheritDoc */
  deactivate() {
    this._disposeReaction?.()
    this._disposeReaction = null
    this._epoch = null

    if (this._options.deactivateMessage || this._options.deactivateEvent) {
      for (const item of this._items) {
        if (item instanceof ChatListMessageItemViewModel) {
          if (this._options.deactivateMessage) {
            this._options.deactivateMessage(item.messageVm)
          }
        } else if (item instanceof ChatListEventItemViewModel) {
          if (this._options.deactivateEvent) {
            this._options.deactivateEvent(item.event)
          }
        }
      }
    }

    this._items = []
    this._eventVmMap.clear()
    this._messageVmMap.clear()
    this._timeMarkerVmMap.clear()
  }

  /**
   * Syncs the chat items based on the new messages input.
   * Note that this relies on both the message items and the event items
   * to be stable objects, otherwise the item VMs will be recreated each time.
   *
   * @private
   * @param inputMessages
   * @param inputEvents
   */
  @action.bound
  private _syncItems(inputMessages: M[], inputEvents: E[]) {
    const itemSet = new Set<ChatListItemViewModel<M, V, E>>()
    const itemArray: ChatListItemViewModel<M, V, E>[] = []
    const maxDurationBetweenSameSenderMessages =
      this._options.maxDurationBetweenSameSenderMessages ?? Infinity

    let currentDayVm: ChatListTimeMarkerViewModel | null = null
    let previousMessageVmInGroup: ChatListMessageItemViewModel<M, V> | null =
      null

    // Indices for message and event arrays
    let messageIndex = 0
    let eventIndex = 0

    // Tracks newly created VMs so we can emit them after we're done.
    const newlyCreatedNonTimeMarkerItems: (
      | ChatListMessageItemViewModel<M, V>
      | ChatListEventItemViewModel<E>
    )[] = []

    // Process items until we've gone through both arrays
    while (
      messageIndex < inputMessages.length ||
      eventIndex < inputEvents.length
    ) {
      let processMessage = false

      // Determine whether to process a message or an event next
      if (messageIndex >= inputMessages.length) {
        // All messages processed, process the next event
        processMessage = false
      } else if (eventIndex >= inputEvents.length) {
        // All events processed, process the next message
        processMessage = true
      } else if (this._options.getEventDate) {
        // Compare dates to determine which to process next
        const messageDate = this._options.getMessageDate(
          inputMessages[messageIndex],
        )
        const eventDate = this._options.getEventDate(inputEvents[eventIndex])

        // If the message date is less than or equal to the event date, process
        // message This ensures that when dates are equal, messages come before
        // events
        processMessage = messageDate.valueOf() <= eventDate.valueOf()
      }

      if (processMessage) {
        // Process the next message
        const message = inputMessages[messageIndex]
        const messageDate = this._options.getMessageDate(message)
        const messageSender = this._options.getMessageSender(message)

        // Check if we need to start a new day.
        if (
          currentDayVm === null ||
          !isSameDay(currentDayVm.date, messageDate)
        ) {
          // Start a new day
          currentDayVm = this._timeMarkerVmMap.get(messageDate) ?? null
          if (currentDayVm === null) {
            currentDayVm = new ChatListTimeMarkerViewModel(messageDate)
            this._timeMarkerVmMap.set(messageDate, currentDayVm)
          }

          itemSet.add(currentDayVm)
          itemArray.push(currentDayVm)

          if (previousMessageVmInGroup) {
            previousMessageVmInGroup.isLastInGroup = true
          }
          previousMessageVmInGroup = null
        }

        let messageVm = this._messageVmMap.get(message) ?? null
        if (!messageVm) {
          const innerMessageVm =
            // If no custom message VM is provided, use the message
            (this._options.createMessageVm?.(message) ?? message) as V
          messageVm = new ChatListMessageItemViewModel(
            this._options.getMessageDate,
            this._options.getMessageSender,
            message,
            innerMessageVm,
          )
          this._options.activateMessage?.(innerMessageVm)
          this._messageVmMap.set(message, messageVm)
          newlyCreatedNonTimeMarkerItems.push(messageVm)
        }

        messageVm.isFirstInGroup =
          !previousMessageVmInGroup ||
          previousMessageVmInGroup.sender !== messageSender ||
          messageDate.valueOf() - previousMessageVmInGroup.date.valueOf() >
            maxDurationBetweenSameSenderMessages
        messageVm.isLastInGroup = false

        if (previousMessageVmInGroup) {
          previousMessageVmInGroup.isLastInGroup = messageVm.isFirstInGroup
        }

        itemSet.add(messageVm)
        itemArray.push(messageVm)
        previousMessageVmInGroup = messageVm
        messageIndex++
      } else if (this._options.getEventDate) {
        // Process the next event
        const event = inputEvents[eventIndex]
        const eventDate = this._options.getEventDate(event)

        // Check if we need a new time group for this event (different day)
        if (currentDayVm === null || !isSameDay(currentDayVm.date, eventDate)) {
          // Start a new time group for a new day
          currentDayVm = this._timeMarkerVmMap.get(eventDate) ?? null
          if (currentDayVm === null) {
            currentDayVm = new ChatListTimeMarkerViewModel(eventDate)
            this._timeMarkerVmMap.set(eventDate, currentDayVm)
          }

          itemSet.add(currentDayVm)
          itemArray.push(currentDayVm)
        }

        // End the current message group if any
        if (previousMessageVmInGroup) {
          previousMessageVmInGroup.isLastInGroup = true
          previousMessageVmInGroup = null
        }

        let eventVm = this._eventVmMap.get(event) ?? null
        if (!eventVm) {
          eventVm = new ChatListEventItemViewModel(
            this._options.getEventDate,
            event,
          )
          this._options.activateEvent?.(event)
          this._eventVmMap.set(event, eventVm)
          newlyCreatedNonTimeMarkerItems.push(eventVm)
        }

        itemSet.add(eventVm)
        itemArray.push(eventVm)

        eventIndex++
      }
    }

    if (previousMessageVmInGroup) {
      previousMessageVmInGroup.isLastInGroup = true
    }

    // Now we need to assign group IDs, but in reverse, so that when we load
    // messages that are older, the group IDs remain the same for the newer
    // messages. This is because the input is ordered by date in ascending order,
    // so loading older messages will come in at the top, and new messages
    // are appended at the bottom, but messages in between should retain their
    // group IDs.
    if (itemArray.length > 0) {
      // Default the epoch item to the last item in the array.
      let epochIndex = itemArray.length - 1

      // We already established an epoch, find the index of the item with the
      // same date, or the closest after it.
      if (this._epoch !== null) {
        epochIndex = binarySearch(itemArray, this._epoch, {
          selector: (item) => item.date,
          closest: true,
        })
      }

      const epochItem = itemArray[epochIndex]

      // Save the epoch for future syncs so it's used as the reference point
      // for group IDs.
      this._epoch = epochItem.date

      // Start by assigning group IDs to items before the epoch, they will
      // be assigned in reverse order.
      let groupId = 0
      for (let i = epochIndex - 1; i >= 0; i--) {
        const item = itemArray[i]
        if (!(item instanceof ChatListMessageItemViewModel)) {
          continue
        }

        if (item.isLastInGroup) {
          groupId++
        }

        item.groupId = groupId === 0 ? `${groupId}` : `-${groupId}`
      }

      // Assign group IDs to items after the epoch, including the epoch item
      // itself.
      groupId = 0
      for (let i = epochIndex; i < itemArray.length; i++) {
        const item = itemArray[i]
        if (!(item instanceof ChatListMessageItemViewModel)) {
          continue
        }

        item.groupId = groupId === 0 ? `${groupId}` : `+${groupId}`

        if (item.isLastInGroup) {
          groupId++
        }
      }
    }

    // Clean up items in maps that aren't referenced in the result
    for (const oldItem of this._items) {
      if (itemSet.has(oldItem)) {
        continue
      }

      switch (true) {
        case oldItem instanceof ChatListTimeMarkerViewModel: {
          this._timeMarkerVmMap.delete(oldItem.date)
          break
        }
        case oldItem instanceof ChatListMessageItemViewModel: {
          this._options.deactivateMessage?.(oldItem.messageVm)
          this._messageVmMap.delete(oldItem.message)
          break
        }
        case oldItem instanceof ChatListEventItemViewModel: {
          this._options.deactivateEvent?.(oldItem.event)
          this._eventVmMap.delete(oldItem.event)
          break
        }
      }
    }

    this._items = itemArray

    // Emit all new non-time marker items
    for (const item of newlyCreatedNonTimeMarkerItems) {
      this._itemAddedSubject.next(item)
    }
  }
}

export type ChatListItemViewModel<M, V, E> =
  | ChatListMessageItemViewModel<M, V>
  | ChatListEventItemViewModel<E>
  | ChatListTimeMarkerViewModel

export class ChatListMessageItemViewModel<M, V = M> {
  key = uniqueId('message-item-view-model-')

  @observable accessor isFirstInGroup = false
  @observable accessor isLastInGroup = false
  @observable accessor groupId = 'unset'

  constructor(
    private readonly _getMessageDate: (message: M) => Date,
    private readonly _getSender: (message: M) => unknown,
    public readonly message: M,
    public readonly messageVm: V,
  ) {}

  get date() {
    return this._getMessageDate(this.message)
  }

  get sender() {
    // NOTE: no need to make it a `@computed`
    return this._getSender(this.message)
  }
}

export class ChatListEventItemViewModel<E> {
  key = uniqueId('event-item-view-model-')

  constructor(
    private readonly _getEventDate: (event: E) => Date,
    public readonly event: E,
  ) {}

  get date() {
    return this._getEventDate(this.event)
  }
}

export class ChatListTimeMarkerViewModel {
  key = uniqueId('time-marker-item-view-model-')

  constructor(public readonly date: Date) {}
}
